<template>
  <section
    class="@container group relative max-w-[950px] rounded-lg bg-white p-2 text-black transition duration-300 hover:scale-[1.03]"
    data-cy-certificate-card
  >
    <div
      class="animation-breath conic-gradient-brand absolute inset-0 rounded-lg blur-3xl"
    />
    <div class="absolute inset-0 overflow-hidden rounded-lg">
      <div class="conic-gradient-brand h-full w-full opacity-80 blur-xl" />
    </div>

    <div
      ref="card"
      class="relative grid place-content-center rounded-lg bg-white p-12"
      :style="{ '--posX': `${elementX}px`, '--posY': `${elementY}px` }"
    >
      <div class="absolute inset-0 overflow-hidden rounded-lg">
        <app-image
          alt=""
          class="absolute inset-0 h-full w-full opacity-10"
          src="/images/topographic_pattern.svg"
        />

        <div
          class="conic-gradient-brand absolute left-0 aspect-square h-full w-1/4 -translate-x-1/2 -rotate-45 opacity-80 mix-blend-screen blur-xl transition-[left] duration-500 group-hover:left-1/2"
        />
      </div>

      <div class="@md:mb-10 mb-6 flex items-center justify-center gap-2">
        <app-image
          alt="Pollen"
          :height="40"
          src="/images/logo_full_color.svg"
          :width="150"
        />
        <template v-if="certificate.session.organization">
          <span class="text-primary-950 mx-2 font-semibold">x</span>

          <app-image
            :alt="certificate.session.organization.name"
            :height="52"
            :src="certificate.session.organization.logo_url"
          />
        </template>

        <div class="border-primary h-8 border-l" />
        <p class="text-primary text-xl font-semibold">
          {{ $t("certificate.label", {}, { locale: locale }) }}
        </p>
      </div>

      <div class="@md:text-lg max-w-[500px] text-balance text-center text-sm">
        <i18n-t keypath="certificate.card.title" :locale="locale" tag="h1">
          <template #name>
            <span
              class="font-title @md:text-3xl mb-3 block text-xl"
              data-cy-certificate-owner-full-name
            >
              {{ certificate.user.full_name }}
            </span>
          </template>
        </i18n-t>

        <i18n-t
          class="@md:mt-6 mt-3"
          keypath="certificate.card.session_label"
          :locale="locale"
          tag="p"
        >
          <template #title>
            <strong data-cy-certificate-course-title>{{ content.title }}</strong
            ><br />
          </template>

          <template #teacher>
            <strong data-cy-certificate-teacher-name>{{
              content.teachers
            }}</strong>
          </template>
        </i18n-t>
      </div>

      <i18n-t
        class="text-subtle @md:my-6 @md:text-sm my-3 text-center font-mono text-xs"
        data-cy-certificate-id
        keypath="certificate.card.issue_date_label"
        :locale="locale"
        tag="p"
      >
        <template #id> #{{ certificate.id }}<br /> </template>
        <template #date>
          {{ issueDate }}
        </template>
      </i18n-t>

      <p class="flex items-end justify-end text-right text-sm">
        <app-image
          alt=""
          aria-hidden="true"
          class="@md:h-[100px] h-16"
          :height="100"
          src="/images/signature.svg"
        />
        <span>
          <strong>Julie Ranty</strong><br />{{
            $t("certificate.card.signature_title")
          }}</span
        >
      </p>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useCertificateContent } from "~/business-areas/certificate/composables/certificate-content.hook";
import type { CertificateView } from "~~/model/certificate.model";

const properties = defineProps<{
  certificate: CertificateView;
}>();

const { locale, issueDate, content } = useCertificateContent(
  properties.certificate,
);

const wrapper = templateRef<HTMLElement>("card");
const { elementX, elementY } = useMouseInElement(wrapper);
</script>
